const STORAGE_FLUSH_TIMEOUT_MS = 200;

// Maps to track pending writes and last committed values. Keys and values are
// always strings. This allows the queue to dedupe redundant writes and avoid
// repeating the same value.
const pendingStorageWrites = new Map();
const lastCommittedStorageValues = new Map();

// Idle callback and timeout identifiers so they can be cancelled when a flush
// is triggered early or rescheduled.
let idleCallbackId = null;
let timeoutId = null;

/**
 * Determine if the current environment has access to window.localStorage. In
 * non‑browser contexts (e.g. server side rendering) these helpers should
 * safely no‑op.
 */
function hasBrowserStorage() {
  return (
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined'
  );
}

/**
 * Cancel any scheduled flush via requestIdleCallback or setTimeout.
 */
function clearScheduledFlush() {
  if (typeof window === 'undefined') return;

  if (
    idleCallbackId != null &&
    typeof window.cancelIdleCallback === 'function'
  ) {
    window.cancelIdleCallback(idleCallbackId);
  }

  if (timeoutId != null) {
    window.clearTimeout(timeoutId);
  }

  idleCallbackId = null;
  timeoutId = null;
}

/**
 * Schedule a flush of pending writes. Uses requestIdleCallback if available
 * with a timeout to ensure writes happen even when the browser is busy. Falls
 * back to setTimeout when requestIdleCallback is not available (e.g. in
 * Safari).
 */
function scheduleFlush() {
  if (!hasBrowserStorage()) return;
  // Avoid scheduling multiple flushes when one is already pending.
  if (idleCallbackId != null || timeoutId != null) return;

  if (typeof window.requestIdleCallback === 'function') {
    idleCallbackId = window.requestIdleCallback(
      () => {
        idleCallbackId = null;
        flushPendingStorageWrites();
      },
      { timeout: STORAGE_FLUSH_TIMEOUT_MS }
    );
    return;
  }

  // Fallback for browsers without requestIdleCallback
  timeoutId = window.setTimeout(() => {
    timeoutId = null;
    flushPendingStorageWrites();
  }, STORAGE_FLUSH_TIMEOUT_MS);
}

/**
 * Queue a raw string value for a localStorage key. The write will happen
 * asynchronously in a future idle time. If a write for the same key with
 * identical value is already queued or has already been committed, the new
 * write is ignored.
 *
 * @param {string} key
 * @param {string|number|boolean|null|undefined} rawValue
 */
export function queueRawStorageWrite(key, rawValue) {
  if (!hasBrowserStorage()) return;

  const normalizedKey = String(key || '');
  if (!normalizedKey) return;

  const nextValue = String(rawValue);
  const pendingValue = pendingStorageWrites.get(normalizedKey);
  const committedValue = lastCommittedStorageValues.get(normalizedKey);

  // If the exact same value is already queued or committed, skip.
  if (pendingValue === nextValue) return;
  if (pendingValue == null && committedValue === nextValue) return;

  pendingStorageWrites.set(normalizedKey, nextValue);
  scheduleFlush();
}

/**
 * Queue a JSON‐serializable value for a localStorage key. Serializes the
 * provided object to JSON and delegates to queueRawStorageWrite. If
 * serialization fails, the write is silently ignored.
 *
 * @param {string} key
 * @param {*} value
 */
export function queueJsonStorageWrite(key, value) {
  try {
    queueRawStorageWrite(key, JSON.stringify(value));
  } catch {
    // ignore serialization failures
  }
}

/**
 * Immediately write a raw string value to localStorage and update the
 * committed value map. Any pending write for this key is removed.
 *
 * @param {string} key
 * @param {string|number|boolean|null|undefined} rawValue
 */
export function writeRawStorageNow(key, rawValue) {
  if (!hasBrowserStorage()) return;

  const normalizedKey = String(key || '');
  if (!normalizedKey) return;

  const nextValue = String(rawValue);
  pendingStorageWrites.delete(normalizedKey);

  try {
    window.localStorage.setItem(normalizedKey, nextValue);
    lastCommittedStorageValues.set(normalizedKey, nextValue);
  } catch {
    // ignore storage failures
  }
}

/**
 * Immediately write a JSON‐serializable value to localStorage. Serializes
 * the provided object and delegates to writeRawStorageNow. If serialization
 * fails, the write is silently ignored.
 *
 * @param {string} key
 * @param {*} value
 */
export function writeJsonStorageNow(key, value) {
  try {
    writeRawStorageNow(key, JSON.stringify(value));
  } catch {
    // ignore serialization/storage failures
  }
}

/**
 * Remove an item from localStorage immediately, clearing any pending queued
 * write for the same key and removing the last committed value. Useful
 * when resetting state to ensure queued writes do not restore old values.
 *
 * @param {string} key
 */
export function removeStorageItemNow(key) {
  if (!hasBrowserStorage()) return;

  const normalizedKey = String(key || '');
  if (!normalizedKey) return;

  pendingStorageWrites.delete(normalizedKey);
  lastCommittedStorageValues.delete(normalizedKey);

  try {
    window.localStorage.removeItem(normalizedKey);
  } catch {
    // ignore storage failures
  }
}

/**
 * Flush all pending queued writes to localStorage immediately. Cancels any
 * scheduled idle or timeout callbacks. Writes are performed synchronously.
 */
export function flushPendingStorageWrites() {
  if (!hasBrowserStorage()) return;

  clearScheduledFlush();

  pendingStorageWrites.forEach((value, key) => {
    try {
      window.localStorage.setItem(key, value);
      lastCommittedStorageValues.set(key, value);
    } catch {
      // ignore storage failures
    }
  });

  pendingStorageWrites.clear();
}